import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { StageGates } from '../components/StageGates';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  FolderKanban, Plus, Search, Filter, MoreVertical, Calendar,
  Clock, Users, Target, CheckCircle2, AlertCircle, FileText,
  MessageSquare, BarChart3, Ticket, FileSignature, DollarSign, AlertTriangle,
  StickyNote, Activity, ArrowRight, Edit, Trash2, Eye,
  ChevronRight, Timer, Milestone, TrendingUp, PlayCircle, Shield
} from 'lucide-react';
import { RecordAttachments } from '../components/RecordAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status colors
const STATUS_COLORS = {
  planning: 'bg-slate-100 text-slate-700',
  active: 'bg-green-100 text-green-700',
  on_hold: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
  critical: 'bg-red-200 text-red-700',
};

// Project Card Component
const ProjectCard = ({ project, onClick, onEdit, onDelete }) => {
  const completion = project.completion_percentage || 0;
  
  return (
    <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => onClick(project)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-black text-[#D4E100] flex items-center justify-center">
              <FolderKanban className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base">{project.name}</h3>
              <p className="text-xs text-muted-foreground">{project.client_name || 'No client'}</p>
            </div>
          </div>
          <Badge className={STATUS_COLORS[project.status] || STATUS_COLORS.planning}>
            {project.status?.replace('_', ' ')}
          </Badge>
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {project.description || 'No description'}
        </p>
        
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{completion}%</span>
          </div>
          <Progress value={completion} className="h-2" />
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold">{project.total_tasks || 0}</p>
            <p className="text-[10px] text-muted-foreground">Tasks</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold">{project.completed_tasks || 0}</p>
            <p className="text-[10px] text-muted-foreground">Done</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold">{project.total_hours?.toFixed(1) || 0}</p>
            <p className="text-[10px] text-muted-foreground">Hours</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold">{project.open_tickets || 0}</p>
            <p className="text-[10px] text-muted-foreground">Tickets</p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{project.end_date ? new Date(project.end_date).toLocaleDateString() : 'No deadline'}</span>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(project); }}>
              <Edit className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(project); }}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Project Detail View Component
const ProjectDetailView = ({ project, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [reasonSaving, setReasonSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [notes, setNotes] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [linkedSales, setLinkedSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Dialog states
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showTimesheetDialog, setShowTimesheetDialog] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showDiscussionDialog, setShowDiscussionDialog] = useState(false);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [ganttData, setGanttData] = useState([]);
  // Reports & BI state
  const [reportKpis, setReportKpis] = useState(null);
  const [reportHealth, setReportHealth] = useState(null);
  const [reportLearners, setReportLearners] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Form defaults
  const emptyTask = { name: '', description: '', assigned_to: '', start_date: '', due_date: '', priority: 'medium', status: 'todo' };
  const emptyTs = { date: new Date().toISOString().slice(0, 10), hours: 1, task_id: '', description: '', billable: true };
  const emptyMs = { name: '', description: '', due_date: '', status: 'pending' };
  const emptyTicket = { title: '', description: '', priority: 'medium', status: 'open', assigned_to: '', category: '' };
  const emptyNote = { content: '', is_pinned: false };
  const emptyDisc = { title: '', content: '' };
  const emptyContract = { name: '', contract_type: 'service', value: 0, start_date: '', end_date: '', status: 'draft', notes: '' };

  const [taskForm, setTaskForm] = useState(emptyTask);
  const [tsForm, setTsForm] = useState(emptyTs);
  const [msForm, setMsForm] = useState(emptyMs);
  const [ticketForm, setTicketForm] = useState(emptyTicket);
  const [noteForm, setNoteForm] = useState(emptyNote);
  const [discForm, setDiscForm] = useState(emptyDisc);
  const [contractForm, setContractForm] = useState(emptyContract);

  useEffect(() => {
    loadProjectData();
    loadUsers();
  }, [project.id]);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {
      console.error('Failed to load users');
    }
  };

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const [dashRes, tasksRes, tsRes, msRes, ticketsRes, notesRes, discRes, contractsRes, actRes, salesRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/dashboard`),
        axios.get(`${API}/tasks?project_id=${project.id}`),
        axios.get(`${API}/projects/${project.id}/timesheets`),
        axios.get(`${API}/projects/${project.id}/milestones`),
        axios.get(`${API}/projects/${project.id}/tickets`),
        axios.get(`${API}/projects/${project.id}/notes`),
        axios.get(`${API}/projects/${project.id}/discussions`),
        axios.get(`${API}/projects/${project.id}/contracts`),
        axios.get(`${API}/projects/${project.id}/activity`),
        axios.get(`${API}/projects/${project.id}/sales`).catch(() => ({ data: [] }))
      ]);

      setDashboardData(dashRes.data);
      setTasks(tasksRes.data);
      setTimesheets(tsRes.data);
      setMilestones(msRes.data);
      setTickets(ticketsRes.data);
      setNotes(notesRes.data);
      setDiscussions(discRes.data);
      setContracts(contractsRes.data);
      setActivities(actRes.data);
      setLinkedSales(salesRes.data);
    } catch (error) {
      console.error('Failed to load project data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const stats = dashboardData?.stats || {};

  // ===== CRUD handlers =====
  const handleCreateTask = async () => {
    if (!taskForm.name) { toast.error('Task name required'); return; }
    try {
      await axios.post(`${API}/tasks`, { ...taskForm, project_id: project.id });
      toast.success('Task created'); setShowTaskDialog(false); setTaskForm(emptyTask); loadProjectData();
    } catch { toast.error('Failed to create task'); }
  };
  const handleDeleteTask = async (id) => {
    try { await axios.delete(`${API}/tasks/${id}`); toast.success('Task deleted'); loadProjectData(); } catch { toast.error('Failed'); }
  };
  const handleCreateTimesheet = async () => {
    if (!tsForm.hours) { toast.error('Hours required'); return; }
    try {
      await axios.post(`${API}/projects/${project.id}/timesheets`, { ...tsForm, project_id: project.id });
      toast.success('Time logged'); setShowTimesheetDialog(false); setTsForm(emptyTs); loadProjectData();
    } catch { toast.error('Failed to log time'); }
  };
  const handleDeleteTimesheet = async (id) => {
    try { await axios.delete(`${API}/projects/${project.id}/timesheets/${id}`); toast.success('Deleted'); loadProjectData(); } catch { toast.error('Failed'); }
  };
  const handleCreateMilestone = async () => {
    if (!msForm.name) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/projects/${project.id}/milestones`, { ...msForm, project_id: project.id });
      toast.success('Milestone created'); setShowMilestoneDialog(false); setMsForm(emptyMs); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const handleToggleMilestone = async (ms) => {
    const newStatus = ms.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API}/projects/${project.id}/milestones/${ms.id}`, { ...ms, status: newStatus, project_id: project.id });
      toast.success(`Milestone ${newStatus}`); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const handleDeleteMilestone = async (id) => {
    try { await axios.delete(`${API}/projects/${project.id}/milestones/${id}`); toast.success('Deleted'); loadProjectData(); } catch { toast.error('Failed'); }
  };
  const handleCreateTicket = async () => {
    if (!ticketForm.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/projects/${project.id}/tickets`, { ...ticketForm, project_id: project.id });
      toast.success('Ticket created'); setShowTicketDialog(false); setTicketForm(emptyTicket); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const handleCreateNote = async () => {
    if (!noteForm.content) { toast.error('Content required'); return; }
    try {
      await axios.post(`${API}/projects/${project.id}/notes`, { ...noteForm, project_id: project.id });
      toast.success('Note added'); setShowNoteDialog(false); setNoteForm(emptyNote); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const handleDeleteNote = async (id) => {
    try { await axios.delete(`${API}/projects/${project.id}/notes/${id}`); toast.success('Deleted'); loadProjectData(); } catch { toast.error('Failed'); }
  };
  const handleCreateDiscussion = async () => {
    if (!discForm.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/projects/${project.id}/discussions`, { ...discForm, project_id: project.id });
      toast.success('Discussion started'); setShowDiscussionDialog(false); setDiscForm(emptyDisc); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const handleCreateContract = async () => {
    if (!contractForm.name) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/projects/${project.id}/contracts`, { ...contractForm, project_id: project.id });
      toast.success('Contract added'); setShowContractDialog(false); setContractForm(emptyContract); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const loadReplies = async (disc) => {
    setSelectedDiscussion(disc);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/discussions/${disc.id}/replies`);
      setReplies(res.data);
    } catch { toast.error('Failed to load replies'); }
  };
  const handleAddReply = async () => {
    if (!replyText.trim() || !selectedDiscussion) return;
    try {
      await axios.post(`${API}/projects/${project.id}/discussions/${selectedDiscussion.id}/replies`, { discussion_id: selectedDiscussion.id, content: replyText });
      setReplyText(''); loadReplies(selectedDiscussion); loadProjectData();
    } catch { toast.error('Failed'); }
  };
  const loadGanttData = async () => {
    try {
      const res = await axios.get(`${API}/projects/${project.id}/gantt`);
      setGanttData(res.data);
    } catch { console.error('Failed to load gantt'); }
  };

  const loadReportsData = async () => {
    setReportLoading(true);
    try {
      const [kpiRes, healthRes, learnerRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/kpis`),
        axios.get(`${API}/projects/${project.id}/reports/health`),
        axios.get(`${API}/projects/${project.id}/learners`).catch(() => ({ data: { learners: [], total_learners: 0 } })),
      ]);
      setReportKpis(kpiRes.data);
      setReportHealth(healthRes.data);
      setReportLearners(learnerRes.data);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setReportLoading(false);
    }
  };

  const downloadAuthed = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const downloadJson = async (url, filename) => {
    try {
      const res = await axios.get(url);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (e) {
      toast.error('Download failed');
    }
  };

  // Tab content components
  const reloadDashboard = async () => {
    try {
      const res = await axios.get(`${API}/projects/${project.id}/dashboard`);
      setDashboardData(res.data);
    } catch (e) { /* noop */ }
  };

  const submitUpdateReason = async () => {
    if (!reasonText.trim()) { toast.error('Please enter a reason'); return; }
    setReasonSaving(true);
    try {
      await axios.post(`${API}/projects/${project.id}/update-reasons`, { reason: reasonText.trim() });
      toast.success('Reason recorded');
      setReasonText('');
      reloadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save reason');
    } finally {
      setReasonSaving(false);
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {dashboardData?.needs_attention && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-500/5" data-testid="project-attention-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-red-700">This project needs attention</p>
              <ul className="text-xs text-red-600 mt-1 space-y-0.5">
                {(dashboardData.attention_reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <Target className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_tasks || 0}</p>
                <p className="text-xs text-muted-foreground">Total Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed_tasks || 0}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_hours?.toFixed(1) || 0}</p>
                <p className="text-xs text-muted-foreground">Hours Logged</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                <Ticket className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.open_tickets || 0}</p>
                <p className="text-xs text-muted-foreground">Open Tickets</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress & Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Task Completion</span>
                  <span className="font-medium">{stats.completion_percentage || 0}%</span>
                </div>
                <Progress value={stats.completion_percentage || 0} className="h-3" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Milestones</span>
                  <span className="font-medium">{stats.completed_milestones || 0}/{stats.total_milestones || 0}</span>
                </div>
                <Progress value={stats.total_milestones ? (stats.completed_milestones / stats.total_milestones) * 100 : 0} className="h-3" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge className={STATUS_COLORS[project.status]}>{project.status?.replace('_', ' ')}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Priority</span>
              <Badge className={PRIORITY_COLORS[project.priority] || PRIORITY_COLORS.medium}>{project.priority}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Start Date</span>
              <span className="text-sm">{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">End Date</span>
              <span className="text-sm">{project.end_date ? new Date(project.end_date).toLocaleDateString() : 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Budget</span>
              <span className="text-sm font-medium">R {project.budget?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Last Updated</span>
              <span className="text-sm" data-testid="project-last-updated">
                {dashboardData?.last_update_at ? new Date(dashboardData.last_update_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '-'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue items, risks & update reasons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="project-overdue-risks-card">
          <CardHeader>
            <CardTitle className="text-base">Overdue & Risks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(dashboardData?.overdue_tasks || []).length === 0 && (dashboardData?.open_risks || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No overdue tasks or open risks</p>
            )}
            {(dashboardData?.overdue_tasks || []).map(t => (
              <div key={t.id} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-500/5 rounded-lg">
                <p className="text-sm font-medium truncate">{t.name}</p>
                <span className="text-xs text-red-600 font-medium shrink-0 ml-2">due {t.due_date}</span>
              </div>
            ))}
            {(dashboardData?.open_risks || []).map(r => (
              <div key={r.id} className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-500/5 rounded-lg">
                <p className="text-sm font-medium truncate">{r.title}</p>
                <Badge variant="outline" className="text-xs shrink-0 ml-2">risk {r.risk_score ?? '-'}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="project-update-reason-card">
          <CardHeader>
            <CardTitle className="text-base">Reason for No Update / Delay</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea rows={2} placeholder="Explain missing updates, delayed milestones or stalled work…"
              value={reasonText} onChange={e => setReasonText(e.target.value)} data-testid="update-reason-input" />
            <Button size="sm" onClick={submitUpdateReason} disabled={reasonSaving} data-testid="update-reason-submit">
              {reasonSaving ? 'Saving…' : 'Submit Reason'}
            </Button>
            {(dashboardData?.update_reasons || []).length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                {(dashboardData.update_reasons || []).map(r => (
                  <div key={r.id} className="text-xs p-2 bg-muted rounded-lg" data-testid={`update-reason-${r.id}`}>
                    <p className="text-foreground">{r.reason}</p>
                    <p className="text-muted-foreground mt-0.5">{r.submitted_by_name} — {new Date(r.created_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Milestones & Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Upcoming Milestones</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.upcoming_milestones?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.upcoming_milestones.map(ms => (
                  <div key={ms.id} className="flex items-center gap-3 p-2 bg-muted rounded-lg">
                    <Milestone className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{ms.name}</p>
                      <p className="text-xs text-muted-foreground">{ms.due_date ? new Date(ms.due_date).toLocaleDateString() : 'No date'}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{ms.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No upcoming milestones</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.slice(0, 5).map(act => (
                    <div key={act.id} className="flex items-start gap-3 text-sm">
                      <Activity className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p>{act.description}</p>
                        <p className="text-xs text-muted-foreground">{act.user_name} • {new Date(act.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderTasks = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Tasks ({tasks.length})</h3>
        <Button onClick={() => setShowTaskDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Add Task
        </Button>
      </div>
      
      <div className="grid gap-3">
        {tasks.map(task => (
          <Card key={task.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox checked={task.status === 'done'} />
                <div>
                  <p className="font-medium">{task.name}</p>
                  <p className="text-xs text-muted-foreground">{task.due_date ? `Due: ${new Date(task.due_date).toLocaleDateString()}` : 'No due date'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                <Badge variant="outline">{task.status?.replace('_', ' ')}</Badge>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteTask(task.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {tasks.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No tasks yet. Create one to get started.</CardContent></Card>
        )}
      </div>
    </div>
  );

  const renderTimesheets = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Timesheets ({timesheets.length})</h3>
        <Button onClick={() => setShowTimesheetDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Log Time
        </Button>
      </div>
      
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="p-3 text-left text-sm font-medium">Date</th>
                <th className="p-3 text-left text-sm font-medium">User</th>
                <th className="p-3 text-left text-sm font-medium">Task</th>
                <th className="p-3 text-left text-sm font-medium">Hours</th>
                <th className="p-3 text-left text-sm font-medium">Billable</th>
                <th className="p-3 text-left text-sm font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map(ts => (
                <tr key={ts.id} className="border-t">
                  <td className="p-3 text-sm">{new Date(ts.date).toLocaleDateString()}</td>
                  <td className="p-3 text-sm">{ts.user_name || 'Unknown'}</td>
                  <td className="p-3 text-sm">{ts.task_name || '-'}</td>
                  <td className="p-3 text-sm font-medium">{ts.hours}h</td>
                  <td className="p-3 text-sm">{ts.billable ? '✓' : '-'}</td>
                  <td className="p-3 text-sm"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTimesheet(ts.id)}><Trash2 className="h-3 w-3" /></Button></td>
                </tr>
              ))}
              {timesheets.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No time entries yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );

  const renderMilestones = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Milestones ({milestones.length})</h3>
        <Button onClick={() => setShowMilestoneDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Add Milestone
        </Button>
      </div>
      
      <div className="grid gap-3">
        {milestones.map(ms => (
          <Card key={ms.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleToggleMilestone(ms)}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${ms.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'}`}>
                  <Milestone className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">{ms.name}</p>
                  <p className="text-xs text-muted-foreground">{ms.due_date ? `Due: ${new Date(ms.due_date).toLocaleDateString()}` : 'No date'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{ms.status}</Badge>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteMilestone(ms.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {milestones.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No milestones yet</CardContent></Card>
        )}
      </div>
    </div>
  );

  const renderTickets = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Tickets ({tickets.length})</h3>
        <Button onClick={() => setShowTicketDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Create Ticket
        </Button>
      </div>
      
      <div className="grid gap-3">
        {tickets.map(ticket => (
          <Card key={ticket.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-muted-foreground">{ticket.ticket_number}</span>
                    <Badge className={PRIORITY_COLORS[ticket.priority]}>{ticket.priority}</Badge>
                  </div>
                  <p className="font-medium">{ticket.title}</p>
                  <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                </div>
                <Badge variant="outline">{ticket.status?.replace('_', ' ')}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        {tickets.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No tickets yet</CardContent></Card>
        )}
      </div>
    </div>
  );

  const renderNotes = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Notes ({notes.length})</h3>
        <Button onClick={() => setShowNoteDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Add Note
        </Button>
      </div>
      
      <div className="grid gap-3">
        {notes.map(note => (
          <Card key={note.id} className={note.is_pinned ? 'border-yellow-300 bg-yellow-50/50' : ''}>
            <CardContent className="p-4">
              <p className="text-sm whitespace-pre-wrap">{note.content}</p>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-muted-foreground">
                  {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                  {note.is_pinned && <Badge variant="outline" className="ml-2">Pinned</Badge>}
                </p>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteNote(note.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {notes.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No notes yet</CardContent></Card>
        )}
      </div>
    </div>
  );

  const renderDiscussions = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Discussions ({discussions.length})</h3>
        <div className="flex gap-2">
          {selectedDiscussion && <Button variant="outline" size="sm" onClick={() => setSelectedDiscussion(null)}>Back to List</Button>}
          <Button onClick={() => setShowDiscussionDialog(true)} size="sm"><Plus className="h-4 w-4 mr-2" /> Start Discussion</Button>
        </div>
      </div>
      {selectedDiscussion ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{selectedDiscussion.title}</CardTitle>
            <CardDescription>{selectedDiscussion.created_by_name} - {new Date(selectedDiscussion.created_at).toLocaleString()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm bg-muted p-3 rounded-lg">{selectedDiscussion.content}</p>
            <div className="border-t pt-4 space-y-3">
              <h4 className="text-sm font-semibold">Replies ({replies.length})</h4>
              <ScrollArea className="max-h-[300px]">
                {replies.map(r => (
                  <div key={r.id} className="p-3 bg-muted/50 rounded-lg mb-2">
                    <p className="text-sm">{r.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">{r.created_by_name} - {new Date(r.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </ScrollArea>
              <div className="flex gap-2">
                <Input placeholder="Write a reply..." value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddReply()} />
                <Button size="sm" onClick={handleAddReply}>Reply</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {discussions.map(disc => (
            <Card key={disc.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadReplies(disc)}>
              <CardContent className="p-4">
                <h4 className="font-medium">{disc.title}</h4>
                <p className="text-sm text-muted-foreground line-clamp-2">{disc.content}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span>{disc.created_by_name}</span>
                  <span>{disc.replies_count || 0} replies</span>
                  <span>{new Date(disc.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {discussions.length === 0 && (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No discussions yet</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );

  const renderContracts = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Contracts ({contracts.length})</h3>
        <Button onClick={() => setShowContractDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" /> Add Contract
        </Button>
      </div>
      
      <div className="grid gap-3">
        {contracts.map(contract => (
          <Card key={contract.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSignature className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{contract.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {contract.contract_type} • R {contract.value?.toLocaleString()}
                  </p>
                </div>
              </div>
              <Badge variant="outline">{contract.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {contracts.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No contracts yet</CardContent></Card>
        )}
      </div>
    </div>
  );

  const renderMySales = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Linked Sales ({linkedSales.length})</h3>
        <Badge variant="outline">Read Only</Badge>
      </div>
      
      <div className="grid gap-3">
        {linkedSales.map(sale => (
          <Card key={sale.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{sale.title || sale.name}</p>
                  <p className="text-xs text-muted-foreground">Value: R {sale.value?.toLocaleString()}</p>
                </div>
              </div>
              <Badge variant="outline">{sale.stage || sale.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {linkedSales.length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No linked sales</CardContent></Card>
        )}
      </div>
    </div>
  );

  const renderGantt = () => {
    const items = ganttData.length > 0 ? ganttData : [...tasks.map(t => ({ id: t.id, name: t.name, type: 'task', start: t.start_date, end: t.due_date, progress: t.status === 'done' ? 100 : t.status === 'in_progress' ? 50 : 0, status: t.status })), ...milestones.map(m => ({ id: m.id, name: m.name, type: 'milestone', start: m.due_date, end: m.due_date, progress: m.status === 'completed' ? 100 : 0, status: m.status }))];
    const validItems = items.filter(i => i.start);
    if (validItems.length === 0) return <Card><CardContent className="p-8 text-center text-muted-foreground">No items with dates to display. Add tasks or milestones with start/due dates.</CardContent></Card>;
    const dates = validItems.flatMap(i => [new Date(i.start), i.end ? new Date(i.end) : new Date(i.start)]);
    const minDate = new Date(Math.min(...dates)); const maxDate = new Date(Math.max(...dates));
    const totalDays = Math.max((maxDate - minDate) / 86400000, 7);
    const getPos = (d) => d ? Math.max(0, (new Date(d) - minDate) / 86400000 / totalDays * 100) : 0;
    const getWidth = (s, e) => { if (!s) return 0; const start = getPos(s); const end = e ? getPos(e) : start + 2; return Math.max(end - start, 2); };
    const barColors = { done: '#16a34a', in_progress: '#2563eb', todo: '#94a3b8', completed: '#16a34a', pending: '#f59e0b', awaiting_feedback: '#8b5cf6' };
    return (
      <div className="space-y-4" data-testid="gantt-chart">
        <div className="flex justify-between items-center"><h3 className="font-semibold">Gantt Chart</h3><Button size="sm" variant="outline" onClick={loadGanttData}><BarChart3 className="h-4 w-4 mr-2" />Refresh</Button></div>
        <Card><CardContent className="p-4 overflow-x-auto">
          <div className="min-w-[600px]">
            <div className="flex items-center text-xs text-muted-foreground border-b pb-2 mb-2">
              <div className="w-[200px] shrink-0 font-medium">Item</div>
              <div className="flex-1 flex justify-between px-2">
                <span>{minDate.toLocaleDateString()}</span>
                <span>{new Date((minDate.getTime() + maxDate.getTime()) / 2).toLocaleDateString()}</span>
                <span>{maxDate.toLocaleDateString()}</span>
              </div>
            </div>
            {validItems.map(item => (
              <div key={item.id} className="flex items-center py-1.5 hover:bg-muted/50 rounded">
                <div className="w-[200px] shrink-0 flex items-center gap-2 pr-2">
                  {item.type === 'milestone' ? <Milestone className="h-3 w-3 text-amber-500" /> : <Target className="h-3 w-3 text-blue-500" />}
                  <span className="text-sm truncate">{item.name}</span>
                </div>
                <div className="flex-1 relative h-6 bg-muted/30 rounded">
                  {item.type === 'milestone' ? (
                    <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rotate-45 border-2" style={{ left: `${getPos(item.start)}%`, backgroundColor: item.status === 'completed' ? '#16a34a' : '#f59e0b', borderColor: item.status === 'completed' ? '#15803d' : '#d97706' }} />
                  ) : (
                    <div className="absolute top-0.5 h-5 rounded-full" style={{ left: `${getPos(item.start)}%`, width: `${getWidth(item.start, item.end)}%`, backgroundColor: barColors[item.status] || '#94a3b8', opacity: 0.8 }}>
                      {item.progress > 0 && <div className="h-full rounded-full" style={{ width: `${item.progress}%`, backgroundColor: barColors[item.status] || '#94a3b8' }} />}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      </div>
    );
  };

  const renderReports = () => {
    const k = reportKpis; const h = reportHealth; const l = reportLearners;
    const projectUrl = `${API}/projects/${project.id}`;
    const STATUS_BADGE = { on_track: 'bg-green-100 text-green-700', at_risk: 'bg-amber-100 text-amber-700', critical: 'bg-red-100 text-red-700' };
    return (
      <div className="space-y-6" data-testid="reports-tab">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div>
            <h3 className="text-lg font-semibold">Reports & BI</h3>
            <p className="text-sm text-muted-foreground">Real-time KPIs, project health, learner tracking and exports for Excel / Google Sheets / Power BI / pivot tables.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="export-excel-btn" onClick={() => downloadAuthed(`${projectUrl}/export/excel`, `${project.name?.replace(/ /g, '_')}_report.xlsx`)}>
              <FileText className="h-4 w-4 mr-1" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" data-testid="export-csv-tasks-btn" onClick={() => downloadAuthed(`${projectUrl}/export/csv?dataset=tasks`, `${project.name?.replace(/ /g, '_')}_tasks.csv`)}>
              <FileText className="h-4 w-4 mr-1" /> CSV: Tasks
            </Button>
            <Button variant="outline" size="sm" data-testid="export-csv-timesheets-btn" onClick={() => downloadAuthed(`${projectUrl}/export/csv?dataset=timesheets`, `${project.name?.replace(/ /g, '_')}_timesheets.csv`)}>
              <FileText className="h-4 w-4 mr-1" /> CSV: Timesheets
            </Button>
            <Button variant="outline" size="sm" data-testid="export-csv-milestones-btn" onClick={() => downloadAuthed(`${projectUrl}/export/csv?dataset=milestones`, `${project.name?.replace(/ /g, '_')}_milestones.csv`)}>
              <FileText className="h-4 w-4 mr-1" /> CSV: Milestones
            </Button>
            <Button variant="outline" size="sm" data-testid="export-powerbi-btn" onClick={() => downloadJson(`${projectUrl}/export/powerbi`, `${project.name?.replace(/ /g, '_')}_powerbi.json`)}>
              <BarChart3 className="h-4 w-4 mr-1" /> Power BI JSON
            </Button>
            <Button size="sm" onClick={loadReportsData} data-testid="refresh-reports-btn">
              <Activity className="h-4 w-4 mr-1" /> {reportLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {!k && !reportLoading && (
          <Card><CardContent className="p-12 text-center">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Load Reports</h3>
            <p className="text-muted-foreground mb-4">Click Refresh to load real-time KPIs, health score and learner data.</p>
            <Button onClick={loadReportsData}><Activity className="h-4 w-4 mr-2" /> Load Reports</Button>
          </CardContent></Card>
        )}

        {k && (
          <>
            {/* Real-time KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Task Completion</p>
                <p className="text-2xl font-bold">{k.task_completion_rate}%</p>
                <Progress value={k.task_completion_rate} className="h-1.5 mt-2" />
                <p className="text-[11px] text-muted-foreground mt-1">{k.done_tasks}/{k.total_tasks} done • {k.overdue_tasks} overdue</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Milestone Hit Rate</p>
                <p className="text-2xl font-bold">{k.milestone_hit_rate}%</p>
                <Progress value={k.milestone_hit_rate} className="h-1.5 mt-2" />
                <p className="text-[11px] text-muted-foreground mt-1">{k.done_milestones}/{k.total_milestones} hit • {k.overdue_milestones} overdue</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Billable Ratio</p>
                <p className="text-2xl font-bold">{k.billable_ratio}%</p>
                <Progress value={k.billable_ratio} className="h-1.5 mt-2" />
                <p className="text-[11px] text-muted-foreground mt-1">{k.billable_hours}h billable / {k.total_hours}h total</p>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Budget Used</p>
                <p className="text-2xl font-bold">{k.budget_utilization}%</p>
                <Progress value={Math.min(k.budget_utilization, 100)} className="h-1.5 mt-2" />
                <p className="text-[11px] text-muted-foreground mt-1">R{(k.actual_spend || 0).toLocaleString()} / R{(k.budget || 0).toLocaleString()}</p>
              </CardContent></Card>
            </div>

            {/* Health & breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1">
                <CardHeader><CardTitle className="text-base">Project Health</CardTitle></CardHeader>
                <CardContent>
                  {h && (
                    <>
                      <div className="text-center mb-4">
                        <p className="text-5xl font-bold">{h.overall_score}</p>
                        <Badge className={STATUS_BADGE[h.overall_status] || ''}>{h.overall_status?.replace('_', ' ')}</Badge>
                      </div>
                      <div className="space-y-3">
                        {h.categories?.map((cat) => (
                          <div key={cat.category}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="font-medium">{cat.category}</span>
                              <span>{cat.score}/100</span>
                            </div>
                            <Progress value={cat.score} className="h-1.5" />
                            <p className="text-[10px] text-muted-foreground mt-1">{cat.detail}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-1">
                <CardHeader><CardTitle className="text-base">Tasks by Status</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(k.task_by_status || {}).map(([s, count]) => (
                      <div key={s} className="flex items-center justify-between text-sm">
                        <Badge className={STATUS_COLORS[s] || 'bg-slate-100'}>{s.replace('_', ' ')}</Badge>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                    {Object.keys(k.task_by_status || {}).length === 0 && <p className="text-sm text-muted-foreground">No tasks</p>}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-semibold mb-2">By Priority</p>
                    <div className="space-y-2">
                      {Object.entries(k.task_by_priority || {}).map(([p, count]) => (
                        <div key={p} className="flex items-center justify-between text-sm">
                          <Badge className={PRIORITY_COLORS[p] || 'bg-slate-100'}>{p}</Badge>
                          <span className="font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-1">
                <CardHeader><CardTitle className="text-base">Team Utilization</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-2">
                      {(k.team_utilization || []).length === 0 && <p className="text-sm text-muted-foreground">No timesheets logged</p>}
                      {(k.team_utilization || []).map((m, idx) => (
                        <div key={idx} className="p-2 bg-muted rounded">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium truncate">{m.name}</span>
                            <span>{(m.total || 0).toFixed(1)}h</span>
                          </div>
                          <div className="flex justify-between text-[11px] text-muted-foreground">
                            <span>Billable</span>
                            <span>{(m.billable || 0).toFixed(1)}h</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Learner Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Learner Tracking ({l?.total_learners || 0})</CardTitle>
                <CardDescription>Students linked to this project (matched by program, department, or training tags).</CardDescription>
              </CardHeader>
              <CardContent>
                {(l?.learners || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No learners linked. Tag the project as "training" or "course" to auto-link active students, or link via student program / department.</p>
                ) : (
                  <ScrollArea className="h-72">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background border-b">
                        <tr className="text-left">
                          <th className="p-2">Name</th>
                          <th className="p-2">Program</th>
                          <th className="p-2">Enrollments</th>
                          <th className="p-2 text-right">Invoiced</th>
                          <th className="p-2 text-right">Outstanding</th>
                        </tr>
                      </thead>
                      <tbody>
                        {l.learners.map((s) => (
                          <tr key={s.id} className="border-b">
                            <td className="p-2 font-medium">{s.full_name}</td>
                            <td className="p-2 text-muted-foreground">{s.program || s.department || '-'}</td>
                            <td className="p-2">{(s.enrollments || []).map((e) => e.course_code || e.course_name).filter(Boolean).join(', ') || '-'}</td>
                            <td className="p-2 text-right">R{(s.total_invoiced || 0).toLocaleString()}</td>
                            <td className="p-2 text-right text-red-600">R{(s.outstanding || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  };

  const renderActivity = () => (
    <div className="space-y-4">
      <h3 className="font-semibold">Activity Log ({activities.length})</h3>
      
      <Card>
        <CardContent className="p-4">
          <ScrollArea className="h-[500px]">
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map(act => (
                  <div key={act.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <Activity className="h-4 w-4 text-muted-foreground mt-1" />
                    <div className="flex-1">
                      <p className="text-sm">{act.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{act.user_name}</span>
                        <span>•</span>
                        <span>{act.entity_type}</span>
                        <span>•</span>
                        <span>{new Date(act.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No activity recorded yet</p>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading project...</div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ChevronRight className="h-4 w-4 rotate-180 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{project.name}</h1>
            <p className="text-sm text-muted-foreground">{project.client_name || 'No client'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={STATUS_COLORS[project.status]}>{project.status?.replace('_', ' ')}</Badge>
          <Badge className={PRIORITY_COLORS[project.priority]}>{project.priority}</Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="overview" className="flex items-center gap-1"><Eye className="h-3 w-3" /> Overview</TabsTrigger>
          <TabsTrigger value="gates" className="flex items-center gap-1" data-testid="gates-tab-trigger"><Shield className="h-3 w-3" /> Stage Gates</TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-1"><Target className="h-3 w-3" /> Tasks</TabsTrigger>
          <TabsTrigger value="timesheets" className="flex items-center gap-1"><Timer className="h-3 w-3" /> Timesheets</TabsTrigger>
          <TabsTrigger value="milestones" className="flex items-center gap-1"><Milestone className="h-3 w-3" /> Milestones</TabsTrigger>
          <TabsTrigger value="tickets" className="flex items-center gap-1"><Ticket className="h-3 w-3" /> Tickets</TabsTrigger>
          <TabsTrigger value="contracts" className="flex items-center gap-1"><FileSignature className="h-3 w-3" /> Contracts</TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-1"><StickyNote className="h-3 w-3" /> Notes</TabsTrigger>
          <TabsTrigger value="discussions" className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Discussions</TabsTrigger>
          <TabsTrigger value="gantt" className="flex items-center gap-1" onClick={loadGanttData}><BarChart3 className="h-3 w-3" /> Gantt</TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-1" onClick={loadReportsData} data-testid="reports-tab-trigger"><TrendingUp className="h-3 w-3" /> Reports & BI</TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-1"><DollarSign className="h-3 w-3" /> My Sales</TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-1"><Activity className="h-3 w-3" /> Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">{renderOverview()}</TabsContent>
        <TabsContent value="gates"><StageGates projectId={project.id} /></TabsContent>
        <TabsContent value="tasks">{renderTasks()}</TabsContent>
        <TabsContent value="timesheets">{renderTimesheets()}</TabsContent>
        <TabsContent value="milestones">{renderMilestones()}</TabsContent>
        <TabsContent value="tickets">{renderTickets()}</TabsContent>
        <TabsContent value="contracts">{renderContracts()}</TabsContent>
        <TabsContent value="notes">{renderNotes()}</TabsContent>
        <TabsContent value="discussions">{renderDiscussions()}</TabsContent>
        <TabsContent value="gantt">{renderGantt()}</TabsContent>
        <TabsContent value="reports">{renderReports()}</TabsContent>
        <TabsContent value="sales">{renderMySales()}</TabsContent>
        <TabsContent value="activity">{renderActivity()}</TabsContent>
      </Tabs>

      {/* ===== CREATE DIALOGS ===== */}
      {/* Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent><DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={taskForm.name} onChange={e => setTaskForm({...taskForm, name: e.target.value})} placeholder="Task name" data-testid="task-name-input" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={taskForm.description} onChange={e => setTaskForm({...taskForm, description: e.target.value})} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Start Date</Label><Input type="date" value={taskForm.start_date} onChange={e => setTaskForm({...taskForm, start_date: e.target.value})} /></div>
              <div className="space-y-1"><Label>Due Date</Label><Input type="date" value={taskForm.due_date} onChange={e => setTaskForm({...taskForm, due_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={v => setTaskForm({...taskForm, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent></Select></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={taskForm.status} onValueChange={v => setTaskForm({...taskForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="todo">To Do</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="done">Done</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1"><Label>Assigned To</Label>
              <Select value={taskForm.assigned_to} onValueChange={v => setTaskForm({...taskForm, assigned_to: v})}><SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button><Button onClick={handleCreateTask} data-testid="create-task-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timesheet Dialog */}
      <Dialog open={showTimesheetDialog} onOpenChange={setShowTimesheetDialog}>
        <DialogContent><DialogHeader><DialogTitle>Log Time</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Date *</Label><Input type="date" value={tsForm.date} onChange={e => setTsForm({...tsForm, date: e.target.value})} data-testid="timesheet-date-input" /></div>
              <div className="space-y-1"><Label>Hours *</Label><Input type="number" step="0.5" min="0.5" value={tsForm.hours} onChange={e => setTsForm({...tsForm, hours: parseFloat(e.target.value) || 0})} data-testid="timesheet-hours-input" /></div>
            </div>
            <div className="space-y-1"><Label>Task (optional)</Label>
              <Select value={tsForm.task_id} onValueChange={v => setTsForm({...tsForm, task_id: v})}><SelectTrigger><SelectValue placeholder="Link to task" /></SelectTrigger>
                <SelectContent>{tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>Description</Label><Input value={tsForm.description} onChange={e => setTsForm({...tsForm, description: e.target.value})} placeholder="What did you work on?" /></div>
            <div className="flex items-center gap-2"><Checkbox checked={tsForm.billable} onCheckedChange={v => setTsForm({...tsForm, billable: v})} /><Label>Billable</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowTimesheetDialog(false)}>Cancel</Button><Button onClick={handleCreateTimesheet} data-testid="create-timesheet-submit">Log Time</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone Dialog */}
      <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
        <DialogContent><DialogHeader><DialogTitle>Create Milestone</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={msForm.name} onChange={e => setMsForm({...msForm, name: e.target.value})} placeholder="Milestone name" data-testid="milestone-name-input" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={msForm.description} onChange={e => setMsForm({...msForm, description: e.target.value})} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Due Date</Label><Input type="date" value={msForm.due_date} onChange={e => setMsForm({...msForm, due_date: e.target.value})} /></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={msForm.status} onValueChange={v => setMsForm({...msForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowMilestoneDialog(false)}>Cancel</Button><Button onClick={handleCreateMilestone} data-testid="create-milestone-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent><DialogHeader><DialogTitle>Create Ticket</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={ticketForm.title} onChange={e => setTicketForm({...ticketForm, title: e.target.value})} placeholder="Ticket title" data-testid="ticket-title-input" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={ticketForm.description} onChange={e => setTicketForm({...ticketForm, description: e.target.value})} rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Priority</Label>
                <Select value={ticketForm.priority} onValueChange={v => setTicketForm({...ticketForm, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={ticketForm.status} onValueChange={v => setTicketForm({...ticketForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent></Select></div>
              <div className="space-y-1"><Label>Category</Label><Input value={ticketForm.category} onChange={e => setTicketForm({...ticketForm, category: e.target.value})} placeholder="e.g. Bug" /></div>
            </div>
            <div className="space-y-1"><Label>Assign To</Label>
              <Select value={ticketForm.assigned_to} onValueChange={v => setTicketForm({...ticketForm, assigned_to: v})}><SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowTicketDialog(false)}>Cancel</Button><Button onClick={handleCreateTicket} data-testid="create-ticket-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent><DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Content *</Label><Textarea value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})} rows={4} placeholder="Write your note..." data-testid="note-content-input" /></div>
            <div className="flex items-center gap-2"><Checkbox checked={noteForm.is_pinned} onCheckedChange={v => setNoteForm({...noteForm, is_pinned: v})} /><Label>Pin this note</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button><Button onClick={handleCreateNote} data-testid="create-note-submit">Add Note</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discussion Dialog */}
      <Dialog open={showDiscussionDialog} onOpenChange={setShowDiscussionDialog}>
        <DialogContent><DialogHeader><DialogTitle>Start Discussion</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={discForm.title} onChange={e => setDiscForm({...discForm, title: e.target.value})} placeholder="Discussion topic" data-testid="discussion-title-input" /></div>
            <div className="space-y-1"><Label>Content *</Label><Textarea value={discForm.content} onChange={e => setDiscForm({...discForm, content: e.target.value})} rows={4} placeholder="Start the conversation..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDiscussionDialog(false)}>Cancel</Button><Button onClick={handleCreateDiscussion} data-testid="create-discussion-submit">Start</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent><DialogHeader><DialogTitle>Add Contract</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name *</Label><Input value={contractForm.name} onChange={e => setContractForm({...contractForm, name: e.target.value})} placeholder="Contract name" data-testid="contract-name-input" /></div>
              <div className="space-y-1"><Label>Type</Label>
                <Select value={contractForm.contract_type} onValueChange={v => setContractForm({...contractForm, contract_type: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="service">Service</SelectItem><SelectItem value="nda">NDA</SelectItem><SelectItem value="sla">SLA</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Value (R)</Label><Input type="number" value={contractForm.value} onChange={e => setContractForm({...contractForm, value: parseFloat(e.target.value) || 0})} /></div>
              <div className="space-y-1"><Label>Start Date</Label><Input type="date" value={contractForm.start_date} onChange={e => setContractForm({...contractForm, start_date: e.target.value})} /></div>
              <div className="space-y-1"><Label>End Date</Label><Input type="date" value={contractForm.end_date} onChange={e => setContractForm({...contractForm, end_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Status</Label>
                <Select value={contractForm.status} onValueChange={v => setContractForm({...contractForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="expired">Expired</SelectItem><SelectItem value="terminated">Terminated</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={contractForm.notes} onChange={e => setContractForm({...contractForm, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowContractDialog(false)}>Cancel</Button><Button onClick={handleCreateContract} data-testid="create-contract-submit">Add Contract</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Portfolio Dashboard component (Reports & BI top-level view)
const PortfolioDashboard = ({ portfolio, loading, onRefresh, onDownload, onOpenProject }) => {
  if (loading && !portfolio) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse">Loading portfolio dashboard...</div></div>;
  }
  if (!portfolio) {
    return (
      <Card><CardContent className="p-12 text-center">
        <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Portfolio Dashboard</h3>
        <p className="text-muted-foreground mb-4">Real-time KPIs across every project — Excel / Google Sheets / Power BI ready.</p>
        <Button onClick={onRefresh}><Activity className="h-4 w-4 mr-2" /> Load Dashboard</Button>
      </CardContent></Card>
    );
  }
  const s = portfolio.summary || {};
  const fmt = (n) => (n || 0).toLocaleString();
  const fmtR = (n) => `R${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const STATUS_COLOR = { planning: 'bg-slate-100 text-slate-700', active: 'bg-green-100 text-green-700', on_hold: 'bg-yellow-100 text-yellow-700', completed: 'bg-blue-100 text-blue-700', cancelled: 'bg-red-100 text-red-700' };
  return (
    <div className="space-y-6" data-testid="portfolio-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold">Portfolio KPI Dashboard</h2>
          <p className="text-sm text-muted-foreground">Live cross-project metrics. Built for Excel, Google Sheets, Power BI and pivot tables.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="portfolio-refresh-btn">
          <Activity className="h-4 w-4 mr-1" /> {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Active / Total</p>
          <p className="text-2xl font-bold">{s.active_projects} / {s.total_projects}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{s.completed_projects} completed</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Budget Used</p>
          <p className="text-2xl font-bold">{fmtR(s.total_spend)}</p>
          <Progress value={s.total_budget ? Math.min((s.total_spend / s.total_budget) * 100, 100) : 0} className="h-1.5 mt-2" />
          <p className="text-[11px] text-muted-foreground mt-1">of {fmtR(s.total_budget)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Task Completion</p>
          <p className="text-2xl font-bold">{s.completion_rate}%</p>
          <Progress value={s.completion_rate} className="h-1.5 mt-2" />
          <p className="text-[11px] text-muted-foreground mt-1">{fmt(s.completed_tasks)}/{fmt(s.total_tasks)} • {fmt(s.overdue_tasks)} overdue</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Billable Hours</p>
          <p className="text-2xl font-bold">{fmt(s.billable_hours)}h</p>
          <Progress value={s.billable_ratio} className="h-1.5 mt-2" />
          <p className="text-[11px] text-muted-foreground mt-1">{s.billable_ratio}% billable / {fmt(s.total_hours)}h total</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><Ticket className="h-5 w-5" /></div>
          <div><p className="text-xl font-bold">{s.open_tickets}</p><p className="text-xs text-muted-foreground">Open Tickets</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"><AlertCircle className="h-5 w-5" /></div>
          <div><p className="text-xl font-bold">{s.critical_tickets}</p><p className="text-xs text-muted-foreground">Critical</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center"><Milestone className="h-5 w-5" /></div>
          <div><p className="text-xl font-bold">{s.done_milestones}/{s.total_milestones}</p><p className="text-xs text-muted-foreground">Milestones</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><FileSignature className="h-5 w-5" /></div>
          <div><p className="text-xl font-bold">{fmtR(s.contract_value)}</p><p className="text-xs text-muted-foreground">Active Contracts</p></div>
        </CardContent></Card>
      </div>

      {/* Top overdue & top billable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertCircle className="h-4 w-4 text-red-500" /> Top Overdue Projects</CardTitle></CardHeader>
          <CardContent>
            {(portfolio.top_overdue || []).length === 0 ? <p className="text-sm text-muted-foreground">No overdue tasks. </p> : (
              <div className="space-y-2">
                {portfolio.top_overdue.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-2 bg-red-50 border border-red-100 rounded">
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.client_name || 'No client'}</p>
                    </div>
                    <Badge variant="destructive">{p.overdue_tasks} overdue</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-600" /> Top Billable Projects</CardTitle></CardHeader>
          <CardContent>
            {(portfolio.top_billable_projects || []).length === 0 ? <p className="text-sm text-muted-foreground">No billable hours logged.</p> : (
              <div className="space-y-2">
                {portfolio.top_billable_projects.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-2 bg-green-50 border border-green-100 rounded">
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.client_name || 'No client'}</p>
                    </div>
                    <span className="text-sm font-semibold">{(p.billable_hours || 0).toFixed(1)}h</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-project export table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-Project Exports</CardTitle>
          <CardDescription>Excel sheets, CSV (Google Sheets / pivot table ready) and Power BI JSON for each project.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="portfolio-projects-table">
            <thead className="border-b">
              <tr className="text-left">
                <th className="p-2">Project</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Budget</th>
                <th className="p-2 text-right">Used %</th>
                <th className="p-2 text-right">Tasks</th>
                <th className="p-2 text-right">Overdue</th>
                <th className="p-2 text-right">Hours</th>
                <th className="p-2 text-right">Completion</th>
                <th className="p-2">Exports</th>
              </tr>
            </thead>
            <tbody>
              {(portfolio.projects || []).map(p => (
                <tr key={p.id} className="border-b hover:bg-muted/50">
                  <td className="p-2">
                    <button className="font-medium text-left hover:underline" onClick={() => onOpenProject({ id: p.id, name: p.name, client_name: p.client_name, status: p.status, priority: p.priority })} data-testid={`portfolio-open-${p.id}`}>
                      {p.name}
                    </button>
                    <p className="text-[11px] text-muted-foreground">{p.client_name || 'No client'}</p>
                  </td>
                  <td className="p-2"><Badge className={STATUS_COLOR[p.status] || ''}>{p.status?.replace('_', ' ')}</Badge></td>
                  <td className="p-2 text-right">{fmtR(p.budget)}</td>
                  <td className="p-2 text-right">{p.budget_utilization}%</td>
                  <td className="p-2 text-right">{p.completed_tasks}/{p.total_tasks}</td>
                  <td className="p-2 text-right">{p.overdue_tasks > 0 ? <span className="text-red-600 font-semibold">{p.overdue_tasks}</span> : '0'}</td>
                  <td className="p-2 text-right">{p.total_hours}h</td>
                  <td className="p-2 text-right w-32">
                    <div className="flex items-center gap-2 justify-end">
                      <Progress value={p.completion_percentage} className="h-1.5 w-16" />
                      <span className="text-xs">{p.completion_percentage}%</span>
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onDownload(p.id, 'excel')} data-testid={`dl-excel-${p.id}`}>Excel</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onDownload(p.id, 'csv', 'tasks')} data-testid={`dl-csv-${p.id}`}>CSV</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onDownload(p.id, 'powerbi')} data-testid={`dl-powerbi-${p.id}`}>Power BI</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(portfolio.projects || []).length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No projects yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Projects Module Page
export const ProjectsModulePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingProjectId, setEditingProjectId] = useState(null)
const [editingName, setEditingName] = useState("")
  const [activeView, setActiveView] = useState('projects');
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    client_name: '',
    manager_id: '',
    budget: 0,
    start_date: '',
    end_date: '',
    status: 'planning',
    priority: 'medium'
  });

  useEffect(() => {
    loadProjects();
    loadClients();
    loadUsers();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects`);
      // Enrich with stats
      const enrichedProjects = await Promise.all(
        res.data.map(async (p) => {
          try {
            const dashRes = await axios.get(`${API}/projects/${p.id}/dashboard`);
            return {
              ...p,
              ...dashRes.data.stats,
              completion_percentage: dashRes.data.stats.completion_percentage
            };
          } catch {
            return p;
          }
        })
      );
      setProjects(enrichedProjects);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {
      console.error('Failed to load clients');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {
      console.error('Failed to load users');
    }
  };

  const loadPortfolio = async () => {
    setPortfolioLoading(true);
    try {
      const res = await axios.get(`${API}/projects/portfolio/dashboard`);
      setPortfolio(res.data);
    } catch (e) {
      toast.error('Failed to load portfolio dashboard');
    } finally {
      setPortfolioLoading(false);
    }
  };

  const portfolioDownload = async (projectId, type, dataset) => {
    try {
      const project = projects.find(p => p.id === projectId) || (portfolio?.projects || []).find(p => p.id === projectId);
      const safeName = (project?.name || 'project').replace(/ /g, '_');
      if (type === 'excel') {
        const res = await axios.get(`${API}/projects/${projectId}/export/excel`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a'); link.href = url; link.download = `${safeName}_report.xlsx`; document.body.appendChild(link); link.click(); link.remove();
      } else if (type === 'csv') {
        const res = await axios.get(`${API}/projects/${projectId}/export/csv?dataset=${dataset || 'tasks'}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a'); link.href = url; link.download = `${safeName}_${dataset || 'tasks'}.csv`; document.body.appendChild(link); link.click(); link.remove();
      } else if (type === 'powerbi') {
        const res = await axios.get(`${API}/projects/${projectId}/export/powerbi`);
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a'); link.href = url; link.download = `${safeName}_powerbi.json`; document.body.appendChild(link); link.click(); link.remove();
      }
      toast.success('Download started');
    } catch (e) { toast.error('Download failed'); }
  };

const handleCreateProject = async () => {
  if (!formData.name) {
    toast.error('Project name is required');
    return;
  }

  try {
    const selectedClient = clients.find(c => c.id === formData.client_id);

    const payload = {
      ...formData,
      client_name: selectedClient?.name || formData.client_name
    };

    if (formData.id) {

      // UPDATE PROJECT
      await axios.put(`${API}/projects/${formData.id}`, payload);
      toast.success('Project updated successfully');

    } else {

      // CREATE PROJECT
      await axios.post(`${API}/projects`, payload);
      toast.success('Project created successfully');

    }

    setShowCreateDialog(false);

    setFormData({
      name: '',
      description: '',
      client_id: '',
      client_name: '',
      manager_id: '',
      budget: 0,
      start_date: '',
      end_date: '',
      status: 'planning',
      priority: 'medium'
    });

    loadProjects();

  } catch (error) {
    toast.error('Failed to save project');
  }
};

  const handleDeleteProject = async (project) => {
    if (!confirm(`Delete project "${project.name}"?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}`);
      toast.success('Project deleted');
      loadProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  // Filter projects
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => p.status === 'active').length;
  const completedProjects = projects.filter(p => p.status === 'completed').length;

  if (selectedProject) {
    return (
      <ProjectDetailView 
        project={selectedProject} 
        onBack={() => { setSelectedProject(null); loadProjects(); }} 
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-module-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Manage your projects, tasks, and team collaboration</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-project-btn">
          <Plus className="h-4 w-4 mr-2" /> New Project
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-black text-[#D4E100] flex items-center justify-center">
              <FolderKanban className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalProjects}</p>
              <p className="text-sm text-muted-foreground">Total Projects</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <PlayCircle className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeProjects}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{completedProjects}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0}%</p>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Tabs: All Projects | Reports & BI Portfolio */}
      <Tabs value={activeView} onValueChange={(v) => { setActiveView(v); if (v === 'portfolio' && !portfolio) loadPortfolio(); }} className="space-y-4">
        <TabsList>
          <TabsTrigger value="projects" data-testid="view-projects-tab"><FolderKanban className="h-3 w-3 mr-1" /> All Projects</TabsTrigger>
          <TabsTrigger value="portfolio" data-testid="view-portfolio-tab"><BarChart3 className="h-3 w-3 mr-1" /> Reports & BI</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search projects..." 
            className="pl-10" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planning">Planning</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse">Loading projects...</div>
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map(project => (
            <ProjectCard 
              key={project.id}
              project={project}
              onClick={setSelectedProject}
              onEdit={(p) => {
  setFormData(p)
  setShowCreateDialog(true)
}}
              onDelete={handleDeleteProject}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Projects Found</h3>
            <p className="text-muted-foreground mb-4">Create your first project to get started</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create Project
            </Button>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="portfolio" className="space-y-4" data-testid="portfolio-content">
          <PortfolioDashboard
            portfolio={portfolio}
            loading={portfolioLoading}
            onRefresh={loadPortfolio}
            onDownload={portfolioDownload}
            onOpenProject={(p) => setSelectedProject(p)}
          />
        </TabsContent>
      </Tabs>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            {formData.id ? "Edit Project" : "Create New Project"}
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter project name"
                />
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Select
  value={String(formData.client_id || "")}
  onValueChange={(v) => setFormData({ ...formData, client_id: v })}
>
  <SelectTrigger>
    <SelectValue placeholder="Select client" />
  </SelectTrigger>

  <SelectContent>
    {clients.map(c => (
      <SelectItem key={c.id} value={String(c.id)}>
        {c.company}
      </SelectItem>
    ))}
  </SelectContent>
</Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Project description"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Project Manager</Label>
                <Select value={formData.manager_id} onValueChange={(v) => setFormData({ ...formData, manager_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input 
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Budget (R)</Label>
                <Input 
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </div>
            {formData.id && (
              <div className="border-t pt-3">
                <RecordAttachments parentType="project" parentId={formData.id} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateProject}>{formData.id ? "Update Project" : "Create Project"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsModulePage;
